"use client"

import { useEffect } from "react"
import { usePathname } from "next/navigation"
import { FALLBACK_TEL_DIGITS, MEDIAHAWK_NUMBER_CLASS } from "."

// Two jobs, both triggered on every Next.js route change:
//
//   1) Re-fire Mediahawk's own scan. `mhct.min.js` only walks the DOM once
//      after first page load — Next.js soft navigation swaps in new
//      <TelNum /> spans that Mediahawk never sees, which is why hard refresh
//      works and clicking a link to the same page does not. Calling
//      `window.mhct.changeMHNumbers()` (the public API exposed by mhct.min.js)
//      runs Mediahawk's internal `Z()` again so new spans get their content
//      replaced and any new `<a href="tel:…">` gets its href rewritten.
//
//   2) Belt-and-braces tel: href rewriter (modelled on Mediahawk's `W()`).
//      Once Mediahawk has hydrated the top-bar TelNum span we read the
//      dynamic href out of it and copy that onto every other tel: anchor
//      whose static URL matches our source number — so even CTAs whose
//      visible label is not the phone number ("Get a Quote", "Call For
//      Quote") still route through the tracking number, regardless of
//      Mediahawk's own pool/autodetect configuration.

const MH_CLASS = MEDIAHAWK_NUMBER_CLASS.split(" ")[0] // "mediahawkNumber9471"

// Google Ads "Tel Number Clicks" conversion. Fired whenever a visitor clicks
// any tel: link. gtag is only defined once the visitor has consented to
// marketing cookies (see tracking-scripts.tsx), so the typeof guard doubles
// as the consent gate. We don't intercept navigation the way Google's
// boilerplate does — a tel: click hands off to the OS dialer without
// unloading the page, and gtag sends the hit via sendBeacon.
const TEL_CLICK_CONVERSION = "AW-658434575/XWp4CMKo298BEI_U-7kC"

function reportTelConversion() {
  const gtag = (window as unknown as {
    gtag?: (...args: unknown[]) => void
  }).gtag
  if (typeof gtag !== "function") return
  gtag("event", "conversion", { send_to: TEL_CLICK_CONVERSION })
}

// Matches the AOV Direct source number in any common notation. Applied to
// the digits portion of a tel: href after stripping the `tel:` scheme.
const SOURCE_TEL_PATTERN =
  /(?:\+?44\s*\(?\s*0?\s*\)?\s*|0044\s*|\(?\s*0\s*\)?\s*)?1202\s*[-\s]?\s*725\s*[-\s]?\s*677/i

// Module-scoped cache: once Mediahawk has handed us a dynamic href we keep
// it so route changes can re-apply it without re-observing.
let cachedDynamicHref: string | null = null

function readDynamicHref(span: HTMLElement): string | null {
  const inner = span.querySelector<HTMLAnchorElement>('a[href^="tel:"]')
  const href = inner?.getAttribute("href")
  if (!href) return null
  const digits = href.replace(/^tel:/, "").replace(/[^\d+]/g, "")
  // Still the static fallback — Mediahawk hasn't run yet
  if (digits === FALLBACK_TEL_DIGITS) return null
  return href
}

function rewriteAll(newHref: string) {
  document
    .querySelectorAll<HTMLAnchorElement>('a[href^="tel:"]')
    .forEach((a) => {
      // Anchors inside a Mediahawk span belong to Mediahawk itself.
      if (a.closest(`.${MH_CLASS}`)) return
      const href = a.getAttribute("href") || ""
      const digits = href.replace(/^tel:/, "")
      if (SOURCE_TEL_PATTERN.test(digits) && href !== newHref) {
        a.setAttribute("href", newHref)
      }
    })
}

// Ask Mediahawk to re-scan the DOM. Public API surfaced by mhct.min.js —
// re-runs `Z()` (span replacement) + `W()` (tel: href rewriting). Safe to
// call before mhct.min.js has loaded — we just no-op in that case and rely
// on Mediahawk's own first-load scan.
function triggerMediahawkRescan() {
  const mhct = (window as unknown as {
    mhct?: { changeMHNumbers?: () => unknown }
  }).mhct
  try {
    mhct?.changeMHNumbers?.()
  } catch {
    /* ignore */
  }
}

export default function TelHrefRewriter() {
  const pathname = usePathname()

  // Delegated tel: click → Google Ads conversion. Mounted once (delegation
  // covers anchors added by later route changes / Mediahawk rewrites).
  // Capture phase so we still see the click if a handler stops propagation.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null
      if (target?.closest?.('a[href^="tel:"]')) reportTelConversion()
    }
    document.addEventListener("click", onClick, true)
    return () => document.removeEventListener("click", onClick, true)
  }, [])

  useEffect(() => {
    // First job: kick Mediahawk to re-process spans on the new route. Use
    // requestAnimationFrame so the new page has actually been committed to
    // the DOM before Mediahawk walks it.
    const raf = requestAnimationFrame(() => triggerMediahawkRescan())

    // Second job: tel: href rewriting fallback. If we already know the
    // dynamic href from a previous route, apply it to anchors on the new
    // page immediately — Mediahawk's W() will do this too once it has
    // run, but copying our cache makes the rewrite synchronous.
    if (cachedDynamicHref) {
      rewriteAll(cachedDynamicHref)
      return () => cancelAnimationFrame(raf)
    }

    let elObserver: MutationObserver | null = null
    let bodyObserver: MutationObserver | null = null

    const attach = (span: HTMLElement) => {
      const tryApply = () => {
        const dyn = readDynamicHref(span)
        if (!dyn) return false
        cachedDynamicHref = dyn
        rewriteAll(dyn)
        return true
      }
      if (tryApply()) return
      elObserver = new MutationObserver(() => {
        if (tryApply()) elObserver?.disconnect()
      })
      elObserver.observe(span, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      })
    }

    const span = document.querySelector<HTMLElement>(`span.${MH_CLASS}`)
    if (span) {
      attach(span)
    } else {
      bodyObserver = new MutationObserver(() => {
        const found = document.querySelector<HTMLElement>(`span.${MH_CLASS}`)
        if (found) {
          bodyObserver?.disconnect()
          attach(found)
        }
      })
      bodyObserver.observe(document.body, { childList: true, subtree: true })
    }

    return () => {
      cancelAnimationFrame(raf)
      elObserver?.disconnect()
      bodyObserver?.disconnect()
    }
  }, [pathname])

  return null
}
